'use client';

import { useEffect } from 'react';
import { z } from 'zod';
import {
  BACKGROUND_EXECUTION_COMPLETED_SSE_EVENT,
  BACKGROUND_EXECUTION_STARTED_SSE_EVENT,
  isCopilotTargetType,
  resolveCopilotBoundTargetId,
  type BackgroundExecutionCompletedPayload,
  type BackgroundExecutionStartedPayload,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { useSSE } from '../app/providers/SseProvider';
import {
  isBackgroundSubagentHitl,
  useBackgroundExecutionStore,
} from '../store/backgroundExecutionStore';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { getAuthHeaders } from '../utils/authHeaders';
import {
  getPendingBackgroundSubagentToolCallIds,
  getPendingHITLReview,
} from '../utils/chatHitlReviewState';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { restorePendingBackgroundSubagentHitl } from '../utils/restorePendingBackgroundSubagentHitl';

const LIVE_SUBAGENT_STATUSES = new Set([
  'starting',
  'running',
  'waiting_input',
]);

const EMPTY_HYDRATE_RACE_MS = 3_000;

const emptyHydrateRecheckTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();
const pendingCardGoneRechecks = new Map<
  string,
  { timer: ReturnType<typeof setTimeout>; toolCallIds: Set<string> }
>();
const mountedHydrateChats = new Set<string>();

function clearPendingCardGoneRecheck(chatId: string): void {
  const pending = pendingCardGoneRechecks.get(chatId);

  if (!pending) {
    return;
  }

  clearTimeout(pending.timer);
  pending.toolCallIds.forEach((toolCallId) =>
    requestedPendingCardToolCallIds.delete(toolCallId),
  );
  pendingCardGoneRechecks.delete(chatId);
}

function schedulePendingCardGoneRecheck({
  chatId,
  toolCallIds,
}: {
  chatId: string;
  toolCallIds: ReadonlySet<string>;
}): void {
  const existing = pendingCardGoneRechecks.get(chatId);
  const mergedToolCallIds = new Set(existing?.toolCallIds);

  toolCallIds.forEach((toolCallId) => mergedToolCallIds.add(toolCallId));

  if (existing) {
    clearTimeout(existing.timer);
    pendingCardGoneRechecks.delete(chatId);
  }

  pendingCardGoneRechecks.set(chatId, {
    toolCallIds: mergedToolCallIds,
    timer: setTimeout(() => {
      pendingCardGoneRechecks.delete(chatId);

      if (!mountedHydrateChats.has(chatId)) {
        mergedToolCallIds.forEach((toolCallId) =>
          requestedPendingCardToolCallIds.delete(toolCallId),
        );
        return;
      }

      void registerPendingCardSubagents({
        chatId,
        toolCallIds: mergedToolCallIds,
        allowEmptyRetry: false,
      });
    }, EMPTY_HYDRATE_RACE_MS),
  });
}

function clearEmptyHydrateRecheck(chatId: string): void {
  const timer = emptyHydrateRecheckTimers.get(chatId);

  if (timer === undefined) {
    return;
  }

  clearTimeout(timer);
  emptyHydrateRecheckTimers.delete(chatId);
}

function scheduleEmptyHydrateRecheck({
  chatId,
  requestedAt,
}: {
  chatId: string;
  requestedAt: number;
}): void {
  clearEmptyHydrateRecheck(chatId);

  const current =
    useBackgroundExecutionStore.getState().pendingExecutionsByToolCallId;
  let delayMs = 0;
  let keptRecent = false;

  for (const execution of current.values()) {
    if (
      execution.copilotThreadId !== chatId ||
      execution.kind !== 'subagent' ||
      execution.terminalStatus
    ) {
      continue;
    }

    const trackedAt = execution.trackedAt ?? 0;
    const ageMs = requestedAt - trackedAt;

    if (ageMs >= EMPTY_HYDRATE_RACE_MS) {
      continue;
    }

    keptRecent = true;
    delayMs = Math.max(delayMs, EMPTY_HYDRATE_RACE_MS - ageMs);
  }

  if (!keptRecent || !mountedHydrateChats.has(chatId)) {
    return;
  }

  const timer = setTimeout(() => {
    emptyHydrateRecheckTimers.delete(chatId);

    if (!mountedHydrateChats.has(chatId)) {
      return;
    }

    void hydrateActiveSubagents(chatId);
  }, delayMs);

  emptyHydrateRecheckTimers.set(chatId, timer);
}

const ActiveSubagentSchema = z.object({
  copilotThreadId: z.string(),
  toolCallId: z.string(),
  taskId: z.string(),
  subagentType: z.string(),
  status: z.enum([
    'starting',
    'running',
    'waiting_input',
    'completed',
    'error',
    'aborted',
  ]),
  applicationId: z.string().optional(),
  surfaceId: z.string().optional(),
  targetType: z.string().optional(),
  pendingConfirmation: z.unknown().optional(),
  announced: z.boolean().optional(),
});

const ActiveSubagentsResponseSchema = z.object({
  subagents: z.array(ActiveSubagentSchema),
});

type ActiveSubagent = z.infer<typeof ActiveSubagentSchema>;

const requestedPendingCardToolCallIds = new Set<string>();

function applySubagentWaitingInputFromMessages({
  chatId,
  messages,
}: {
  chatId: string;
  messages: TerraUIMessage[] | undefined;
}): void {
  if (!messages?.length) {
    return;
  }

  const backgroundToolCallIds =
    getPendingBackgroundSubagentToolCallIds(messages);
  const toolCallIds = new Set(backgroundToolCallIds);
  const parentHitl = getPendingHITLReview(messages);

  if (parentHitl) {
    for (const id of parentHitl.hitlMessageIds) {
      toolCallIds.add(id);
    }
  }

  if (toolCallIds.size === 0) {
    return;
  }

  const store = useBackgroundExecutionStore.getState();

  for (const toolCallId of toolCallIds) {
    const existing = store.getPendingExecution(toolCallId);

    if (!existing || existing.kind !== 'subagent' || existing.waitingInput) {
      continue;
    }

    store.setPendingExecution({
      ...existing,
      waitingInput: true,
    });
  }

  requestUnknownPendingCardSubagents({
    chatId,
    toolCallIds: backgroundToolCallIds,
  });
}

function requestUnknownPendingCardSubagents({
  chatId,
  toolCallIds,
}: {
  chatId: string;
  toolCallIds: string[];
}): void {
  const store = useBackgroundExecutionStore.getState();
  const unknownToolCallIds = toolCallIds.filter(
    (toolCallId) =>
      !store.getPendingExecution(toolCallId) &&
      !requestedPendingCardToolCallIds.has(toolCallId),
  );

  if (!chatId || unknownToolCallIds.length === 0) {
    return;
  }

  unknownToolCallIds.forEach((toolCallId) =>
    requestedPendingCardToolCallIds.add(toolCallId),
  );
  void registerPendingCardSubagents({
    chatId,
    toolCallIds: new Set(unknownToolCallIds),
  });
}

async function registerPendingCardSubagents({
  chatId,
  toolCallIds,
  allowEmptyRetry = true,
}: {
  chatId: string;
  toolCallIds: ReadonlySet<string>;
  allowEmptyRetry?: boolean;
}): Promise<void> {
  const liveRuns = await fetchLiveSubagentRuns(chatId);

  if (!liveRuns) {
    toolCallIds.forEach((toolCallId) =>
      requestedPendingCardToolCallIds.delete(toolCallId),
    );

    return;
  }

  const liveToolCallIds = new Set(liveRuns.map((run) => run.toolCallId));

  liveRuns
    .filter((run) => toolCallIds.has(run.toolCallId))
    .forEach(registerSubagentRun);

  const missingToolCallIds = [...toolCallIds].filter(
    (toolCallId) => !liveToolCallIds.has(toolCallId),
  );

  if (missingToolCallIds.length === 0) {
    return;
  }

  if (liveRuns.length === 0 && allowEmptyRetry) {
    schedulePendingCardGoneRecheck({ chatId, toolCallIds });
    return;
  }

  missingToolCallIds.forEach((toolCallId) => {
    markPendingCardSubagentGone({ chatId, toolCallId });
    requestedPendingCardToolCallIds.delete(toolCallId);
  });
}

function markPendingCardSubagentGone({
  chatId,
  toolCallId,
}: {
  chatId: string;
  toolCallId: string;
}): void {
  const store = useBackgroundExecutionStore.getState();

  if (store.getPendingExecution(toolCallId)) {
    return;
  }

  store.setPendingExecution({
    copilotThreadId: chatId,
    agentThreadId: toolCallId,
    toolCallId,
    applicationId: '',
    agentType: 'sub-agent',
    kind: 'subagent',
    terminalStatus: 'completed',
  });
}

function registerSubagentRun(run: ActiveSubagent): void {
  requestedPendingCardToolCallIds.delete(run.toolCallId);

  const store = useBackgroundExecutionStore.getState();
  const targetType = isCopilotTargetType(run.targetType)
    ? run.targetType
    : undefined;
  const boundTargetId =
    resolveCopilotBoundTargetId({
      applicationId: run.applicationId,
      surfaceId: run.surfaceId,
      targetType,
    }) ??
    run.applicationId ??
    run.surfaceId ??
    '';

  const localWaiting =
    store.getPendingExecution(run.toolCallId)?.waitingInput === true;
  const messages = useChatInstancesStore
    .getState()
    .chats.get(run.copilotThreadId)?.messages;
  const hasUndecidedCard = messages
    ? getPendingBackgroundSubagentToolCallIds(messages).includes(run.toolCallId)
    : false;
  const keepLocalHitlWait =
    localWaiting && (run.announced !== true || hasUndecidedCard);

  store.setPendingExecution({
    copilotThreadId: run.copilotThreadId,
    agentThreadId: run.taskId,
    toolCallId: run.toolCallId,
    applicationId: boundTargetId,
    surfaceId: run.surfaceId,
    targetType: run.targetType,
    agentType: run.subagentType,
    kind: 'subagent',
    waitingInput:
      run.status === 'waiting_input' || keepLocalHitlWait ? true : undefined,
    pausedOnParentTurn: run.announced === true ? undefined : true,
    terminalStatus: undefined,
  });

  if (
    run.status === 'waiting_input' &&
    isBackgroundSubagentHitl(run.pendingConfirmation)
  ) {
    restorePendingBackgroundSubagentHitl({
      chatId: run.copilotThreadId,
      toolCallId: run.toolCallId,
      confirmation: run.pendingConfirmation,
    });
  }
}

function dropOmittedSubagentPendings({
  chatId,
  keepToolCallIds,
}: {
  chatId: string;
  keepToolCallIds: ReadonlySet<string>;
}): void {
  const current =
    useBackgroundExecutionStore.getState().pendingExecutionsByToolCallId;
  const next = new Map(current);
  let changed = false;

  for (const [toolCallId, execution] of current.entries()) {
    if (
      execution.copilotThreadId !== chatId ||
      execution.kind !== 'subagent' ||
      keepToolCallIds.has(toolCallId) ||
      execution.terminalStatus
    ) {
      continue;
    }

    next.delete(toolCallId);
    changed = true;
  }

  if (!changed) {
    return;
  }

  useBackgroundExecutionStore.setState({
    pendingExecutionsByToolCallId: next,
    pendingRevision: useBackgroundExecutionStore.getState().pendingRevision + 1,
  });
}

function dropStaleLocalSubagentsOnEmptyList({
  chatId,
  now,
}: {
  chatId: string;
  now: number;
}): void {
  const current =
    useBackgroundExecutionStore.getState().pendingExecutionsByToolCallId;
  const keepToolCallIds = new Set<string>();

  for (const [toolCallId, execution] of current.entries()) {
    if (
      execution.copilotThreadId !== chatId ||
      execution.kind !== 'subagent' ||
      execution.terminalStatus
    ) {
      continue;
    }

    const trackedAt = execution.trackedAt ?? 0;

    if (now - trackedAt < EMPTY_HYDRATE_RACE_MS) {
      keepToolCallIds.add(toolCallId);
    }
  }

  dropOmittedSubagentPendings({ chatId, keepToolCallIds });
}

export function rehydrateAfterFailedSubagentAbort({
  chatId,
  error,
}: {
  chatId: string;
  error: unknown;
}): void {
  console.error('Failed to abort subagent:', error);
  void hydrateActiveSubagents(chatId);
}

async function fetchLiveSubagentRuns(
  chatId: string,
): Promise<ActiveSubagent[] | undefined> {
  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(
      `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${chatId}/active-subagents`,
      {
        credentials: 'include',
        headers: { ...authHeaders },
        method: 'GET',
      },
    );

    if (!response.ok) {
      console.warn(
        'Failed to hydrate active subagents; continuing with local store',
        { chatId, status: response.status },
      );

      return undefined;
    }

    const parsed = ActiveSubagentsResponseSchema.safeParse(
      await response.json(),
    );

    if (!parsed.success) {
      console.warn(
        'Failed to parse active subagents; continuing with local store',
        { chatId },
      );

      return undefined;
    }

    return parsed.data.subagents.filter((run) =>
      LIVE_SUBAGENT_STATUSES.has(run.status),
    );
  } catch (error: unknown) {
    console.warn(
      'Failed to hydrate active subagents; continuing with local store',
      {
        chatId,
        error: error instanceof Error ? error.message : String(error),
      },
    );

    return undefined;
  }
}

async function hydrateActiveSubagents(chatId: string): Promise<void> {
  const requestedAt = Date.now();
  const liveRuns = await fetchLiveSubagentRuns(chatId);

  if (!liveRuns) {
    return;
  }

  const keepToolCallIds = new Set(liveRuns.map((run) => run.toolCallId));

  liveRuns.forEach(registerSubagentRun);

  if (keepToolCallIds.size > 0) {
    clearEmptyHydrateRecheck(chatId);
    dropOmittedSubagentPendings({ chatId, keepToolCallIds });
  } else {
    dropStaleLocalSubagentsOnEmptyList({ chatId, now: requestedAt });
    scheduleEmptyHydrateRecheck({ chatId, requestedAt });
  }
}

export function useBackgroundSubagentLifecycle({
  chatId,
}: {
  chatId: string;
}): void {
  const { subscribe, subscribeReconnect } = useSSE();
  const messages = useChatInstancesStore(
    (state) => state.chats.get(chatId)?.messages,
  );

  useEffect(() => {
    applySubagentWaitingInputFromMessages({ chatId, messages });

    return useBackgroundExecutionStore.subscribe((state, previous) => {
      if (state.pendingRevision !== previous.pendingRevision) {
        applySubagentWaitingInputFromMessages({
          chatId,
          messages: useChatInstancesStore.getState().chats.get(chatId)
            ?.messages,
        });
      }
    });
  }, [chatId, messages]);

  useEffect(() => {
    if (!chatId) {
      return;
    }

    mountedHydrateChats.add(chatId);
    void hydrateActiveSubagents(chatId);

    const unsubscribeStarted = subscribe(
      BACKGROUND_EXECUTION_STARTED_SSE_EVENT,
      (data: BackgroundExecutionStartedPayload) => {
        if (data.copilotThreadId !== chatId || data.kind !== 'subagent') {
          return;
        }

        const store = useBackgroundExecutionStore.getState();
        const existing = store.getPendingExecution(data.toolCallId);

        store.setPendingExecution({
          copilotThreadId: data.copilotThreadId,
          agentThreadId: data.agentThreadId,
          toolCallId: data.toolCallId,
          applicationId: data.applicationId,
          agentType: data.agentType,
          kind: 'subagent',
          waitingInput: existing?.waitingInput,
          pausedOnParentTurn: existing?.pausedOnParentTurn,
          terminalStatus: undefined,
        });
      },
    );

    const unsubscribeCompleted = subscribe(
      BACKGROUND_EXECUTION_COMPLETED_SSE_EVENT,
      (data: BackgroundExecutionCompletedPayload) => {
        if (data.copilotThreadId !== chatId || data.kind !== 'subagent') {
          return;
        }

        useBackgroundExecutionStore.getState().markSubagentTerminal({
          agentThreadId: data.agentThreadId,
          terminalStatus: 'completed',
        });
      },
    );

    const unsubscribeReconnect = subscribeReconnect(() => {
      void hydrateActiveSubagents(chatId);
    });

    return () => {
      mountedHydrateChats.delete(chatId);
      clearEmptyHydrateRecheck(chatId);
      clearPendingCardGoneRecheck(chatId);
      unsubscribeStarted();
      unsubscribeCompleted();
      unsubscribeReconnect();
    };
  }, [chatId, subscribe, subscribeReconnect]);
}
